import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
    DataSourceQuery,
    DataSourceQueryResult,
    DataSourceRequest,
} from 'custom_hooks/data_hooks/data_source/data_source';
import { useCallback } from 'react';
import { createRange, unique } from 'utils/array';
import { promiseAllBatched } from 'utils/promise';
import { Modify } from 'utils/types';

export type FilterExpr = string | undefined;
export type FilterValue = string | number | boolean | Date;
export type FilerEscapeRuleType = 'default' | 'like' | 'in';

/**
 * Converts filter expression values to a valid API filter string and escapes any needed characters.
 * * Dates are converted to JSON.
 * * Numbers and booleans are converted to strings.
 * * All special characters are escaped based on the the supplied escape rules.
 *
 * @param value The value to convert.
 * @param escapeRules How to handle special characters (defaults to `'default'`):
 *   * `'default'` - escape `\` and `'`.
 *   * `'like'` - escape `\`, `'`, `%`, and `_`.
 *   * `'in'` - escape `\`, `'`, and `|`.
 */
export function escapeFilterValue(
    value: FilterValue,
    escapeRules: FilerEscapeRuleType,
): string {
    const strValue = value instanceof Date ? value.toJSON() : `${value}`;
    const baseEscaped = strValue.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

    if (escapeRules === 'like') {
        return baseEscaped.replace(/%/g, '\\%').replace(/_/g, '\\_');
    }
    if (escapeRules === 'in') {
        return baseEscaped.replace(/\|/g, '\\|');
    }
    return baseEscaped;
}

export const Filter = {
    /**
     * Builds a "eq" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * eq('Foo', 'bar') === "Foo eq 'bar'"
     */
    eq: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} eq '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "neq" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * neq('Foo', 'bar') === "Foo neq 'bar'"
     */
    neq: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} neq '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "lt" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * lt('Foo', 1) === "Foo lt '1'"
     */
    lt: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} lt '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "leq" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * leq('Foo', 1) === "Foo leq '1'"
     */
    leq: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} leq '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "gt" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * gt('Foo', 1) === "Foo gt '1'"
     */
    gt: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} gt '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "geq" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * geq('Foo', 1) === "Foo geq '1'"
     */
    geq: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} geq '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds a "like" (starts with) filter expression and escapes all `%` with backslashes.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * likeStartsWith('Foo', 'abc') === "Foo like 'abc%'"
     */
    startsWith: (fieldName: string, value: string): FilterExpr =>
        `${fieldName} like '${escapeFilterValue(value, 'like')}%'`,

    /**
     * Builds a "like" (ends with) filter expression and escapes all `%` with backslashes.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * likeEndsWith('Foo', 'abc') === "Foo like '%abc'"
     */
    endsWith: (fieldName: string, value: string): FilterExpr =>
        `${fieldName} like '%${escapeFilterValue(value, 'like')}'`,

    /**
     * Builds a "like" (contains) filter expression and escapes all `%` with backslashes.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * likeContains('Foo', 'abc') === "Foo like '%abc%'"
     */
    contains: (fieldName: string, value: string): FilterExpr =>
        `${fieldName} like '%${escapeFilterValue(value, 'like')}%'`,

    /**
     * Builds a case insensitive (eq) filter expression and escapes all `%` with backslashes.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * like('Foo', 'abc') === "Foo like 'abc'"
     */
    caseInsensitiveEq: (fieldName: string, value: string): FilterExpr =>
        `${fieldName} like '${escapeFilterValue(value, 'like')}'`,

    /**
     * Builds an "in" filter expression.  Returns `undefined` when an empty values array is supplied.
     * Returns "eq" filter expression when only one value is supplied.
     * Multiple values are stitched together with `|`.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * // No values
     * inList('Count', []) === undefined
     * // One value converted to "eq" expression
     * inList('Count', [1]) === "Count eq '1'"
     * // Multiple values
     * inList('Count', [1,2]) === "Count in '1|2'"
     */
    in: (fieldName: string, values: FilterValue[]): FilterExpr => {
        switch (values.length) {
            case 0:
                return undefined;
            case 1:
                return Filter.eq(fieldName, values[0]);
            default:
                return `${fieldName} in '${values
                    .map((value) => escapeFilterValue(value, 'in'))
                    .join('|')}'`;
        }
    },

    /**
     * Builds a "anyEq" filter expression.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * anyEq('Foo', 'bar') === "Foo anyeq 'bar'"
     */
    anyEq: (fieldName: string, value: FilterValue): FilterExpr =>
        `${fieldName} anyeq '${escapeFilterValue(value, 'default')}'`,

    /**
     * Builds an "anyIn" filter expression.  Returns `undefined` when an empty values array is supplied.
     * Returns "anyEq" filter expression when only one value is supplied.
     * Multiple values are stitched together with `|`.
     *
     * @param fieldName The name of the field.
     * @param value The value of the field.
     * @example
     * // No values
     * anyIn('Count', []) === undefined
     * // One value converted to "anyEq" expression
     * anyIn('Count', [1]) === "Count anyeq '1'"
     * // Multiple values
     * anyIn('Count', [1,2]) === "Count in '1|2'"
     */
    anyIn: (fieldName: string, values: FilterValue[]): FilterExpr => {
        switch (values.length) {
            case 0:
                return undefined;
            case 1:
                return Filter.anyEq(fieldName, values[0]);
            default:
                return `${fieldName} anyin '${values
                    .map((value) => escapeFilterValue(value, 'in'))
                    .join('|')}'`;
        }
    },

    /**
     * Joins filter expressions with " AND ".  All `undefined`s are ignored.
     * Returns `undefined` when no non-undefined parts are supplied.
     *
     * @param parts The expressions to combine.
     * @example
     * // Returns "Foo eq '1'" when useFoo is true and useBar is false
     * // Returns "Bar eq '2'" when useFoo is false and useBar is true
     * // Returns "Foo eq '1' AND Bar eq '2'" when useFoo and useBar are both true
     * // Returns undefined when useFoo and useBar are both false
     * and(
     *     useFoo && eq('Foo', '1'),
     *     useBar && eq('Bar', '2')
     * )
     */
    and: (...parts: FilterExpr[]): FilterExpr => {
        const finalParts = parts.filter((e) => e !== undefined);
        switch (finalParts.length) {
            case 0:
                return undefined;
            case 1:
                return finalParts[0];
            default:
                return `(${finalParts.join(' AND ')})`;
        }
    },

    /**
     * Joins filter expressions with " OR ".  All `undefined`s are ignored.
     * Returns `undefined` when no non-undefined parts are supplied.
     *
     * @param parts The expressions to combine.
     * @example
     * // Returns "Foo eq '1'" when useFoo is true and useBar is false
     * // Returns "Bar eq '2'" when useFoo is false and useBar is true
     * // Returns "Foo eq '1' OR Bar eq '2'" when either useFoo or useBar are true
     * // Returns undefined when useFoo and useBar are both false
     * or(
     *     useFoo || eq('Foo', '1'),
     *     useBar || eq('Bar', '2')
     * )
     */
    or: (...parts: FilterExpr[]): FilterExpr => {
        const finalParts = parts.filter((e) => e !== undefined);
        switch (finalParts.length) {
            case 0:
                return undefined;
            case 1:
                return finalParts[0];
            default:
                return `(${finalParts.join(' OR ')})`;
        }
    },
    /**
     * Builds a "isnotnull" filter expression.
     *
     * @param fieldName The name of the field.
     * @example
     * notNull('Foo') === "Foo isnotnull"
     */
    isNotNull: (fieldName: string): FilterExpr => `${fieldName} isnotnull`,
    /**
     * Builds an "isnull" filter expression.
     *
     * @param fieldName The name of the field.
     * @example
     * isNull('Foo') === "Foo isnull"
     */
    isNull: (fieldName: string): FilterExpr => `${fieldName} isnull`,
};

export type OrderByDirection = 'asc' | 'desc';

export type ExpandedOrderByConfig = Readonly<{
    field: string;
    dir: OrderByDirection;
}>;

export type OrderByConfig = string | ExpandedOrderByConfig;

export type OrderByExpr = string | undefined;

export type OrderByOptions = Readonly<{
    /**
     * Fields to invert sort direction.
     */
    invert?: string[];

    /**
     * Fields to convert `desc` sort direction to `desc nulls last`.
     */
    descNullsLast?: string[];

    /**
     * Fields to convert `asc` sort direction to `asc nulls first`.
     */
    ascNullsFirst?: string[];
}>;

/**
 * Builds a multi-order by expression.
 * If only a field name is supplied, sort direction defaults to `asc`.
 * Returns `undefined` if no config is supplied.
 *
 * @param orderBys Order by config options.
 * @param options Additional sorting options.
 * @example
 *
 * // Single sort
 * orderBy(['Foo']) // asc
 * orderBy([{ field: 'Foo', dir: 'asc' }])
 * orderBy([{ field: 'Foo', dir: 'desc' }])
 * orderBy([{ field: 'Foo', dir: 'descNullsLast' }])
 *
 * // Multi-sort
 * orderBy([
 *     'Bar',
 *     { field: 'Baz', dir: 'desc' },
 *     { field: 'Qux', dir: 'descNullsLast' }
 * ])
 */
export function buildOrderBy(
    orderBys: OrderByConfig[],
    options?: OrderByOptions,
): OrderByExpr {
    if (orderBys.length === 0) {
        return undefined;
    }
    return orderBys
        .map<ExpandedOrderByConfig>((cfg) =>
            typeof cfg === 'object' ? cfg : { field: cfg, dir: 'asc' },
        )
        .map((cfg) => {
            const invert = options?.invert?.includes(cfg.field) ?? false;

            return invert
                ? { field: cfg.field, dir: cfg.dir === 'asc' ? 'desc' : 'asc' }
                : cfg;
        })
        .map(({ field, dir }) => {
            const descNullsLast =
                (options?.descNullsLast?.includes(field) ?? false) && dir === 'desc';

            const ascNullsFirst =
                (options?.ascNullsFirst?.includes(field) ?? false) && dir === 'asc';

            return descNullsLast
                ? `${field} desc nulls last`
                : ascNullsFirst
                  ? `${field} asc nulls first`
                  : `${field} ${dir}`;
        })
        .join(',');
}

/**
 * Builds a multi-order by expression.
 * If only a field name is supplied, sort direction defaults to `asc`.
 * Returns `undefined` if no config is supplied.
 *
 * @param orderBys Order by config options.
 * @example
 *
 * // Single sort
 * orderBy(['Foo']) // asc
 * orderBy([{ field: 'Foo', dir: 'asc' }])
 * orderBy([{ field: 'Foo', dir: 'desc' }])
 *
 * // Multi-sort
 * orderBy([
 *     'Bar',
 *     { field: 'Baz', dir: 'desc' },
 * ])
 */
export function buildDataPlatformOrderBy(orderBys: OrderByConfig[]): OrderByExpr {
    if (orderBys.length === 0) {
        return undefined;
    }
    return orderBys
        .map<ExpandedOrderByConfig>((cfg) =>
            typeof cfg === 'object' ? cfg : { field: cfg, dir: 'asc' },
        )
        .map(({ field, dir }) => {
            return dir === 'asc' ? `${field}` : `-${field}`;
        })
        .join(',');
}

type PagedResult<TData> = Readonly<{
    Result: TData[];
    Total: number;
    CurrentPage: number;
    TotalPages: number;
}>;

/**
 * Converts a sdClient paged response into a data source result.
 *
 * @param response A paged result or a paged result promise.
 */
export async function toDataSourceResult<TData>(
    response: Promise<PagedResult<TData>> | PagedResult<TData>,
): Promise<DataSourceQueryResult<TData>> {
    const res = response instanceof Promise ? await response : response;

    return {
        results: res.Result,
        totalPages: res.TotalPages,
        totalResults: res.Total,
        currentPage: res.CurrentPage,
    };
}

type DataPlatformPagedResult<TData> = Readonly<{
    data: TData[];
    metadata: DataPlatformApi.PaginationMetadata;
}>;

/**
 * Converts a data platform paged response into a data source result.
 *
 * @param response A paged result or a paged result promise.
 */
export async function toDataPlatformDataSourceResult<TData>(
    response: Promise<DataPlatformPagedResult<TData>> | DataPlatformPagedResult<TData>,
): Promise<DataSourceQueryResult<TData>> {
    const res = response instanceof Promise ? await response : response;

    return {
        results: res.data,
        currentPage: Math.floor(res.metadata.offset / res.metadata.limit) + 1,
        totalPages: Math.ceil(res.metadata.total / res.metadata.limit),
        totalResults: res.metadata.total,
    };
}

export type UseQueryAllOptions<TData> = UseQueryOptions<TData[]> &
    Readonly<{
        batchSize?: number;
    }>;

export type UseQueryAllResult<TData> = Readonly<{
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    data: TData[] | undefined;
    refetch: () => Promise<void>;
}>;

/**
 * Query hook similar to `useQuery` used to load all pages of data from the API.
 * Will continue to call query callback until all results have been retrieved from the API.
 *
 * @param key Query key
 * @param query Function to get paged data.
 * @param param2 Additional options.
 */
export function useQueryAll<TData, TQueryKey extends QueryKey = QueryKey>(
    key: TQueryKey,
    query: (page: number) => Promise<PagedResult<TData>>,
    options?: Omit<UseQueryAllOptions<TData>, 'queryKey' | 'queryFn'>,
): UseQueryAllResult<TData> {
    const {
        isFetching,
        isError,
        error,
        refetch: refetchData,
        data,
    } = useQuery<TData[]>({
        queryKey: key,
        queryFn: () => queryAll<TData>((page) => query(page), options?.batchSize),
        ...options,
    });

    const refetch = useCallback(async () => {
        await refetchData();
    }, [refetchData]);

    const result: UseQueryAllResult<TData> = {
        isLoading: isFetching,
        isError,
        error,
        refetch,
        data,
    };

    return result;
}

export type PageRequestProps<T> = Modify<
    Readonly<{
        /**
         * The page to fetch.
         */
        page: number;
        /**
         * The page size defaults to api default if not supplied
         */
        pageSize?: number;
    }>,
    T
>;

export async function drainQuery<TData, TFilter>(
    query: DataSourceQuery<TData, TFilter>,
    request: Pick<DataSourceRequest<TFilter>, 'filter' | 'pageLimit'>,
): Promise<DataSourceQueryResult<TData>[]> {
    const startPage = 1;

    const firstResult = await query({ ...request, sort: [], page: startPage });

    const queryPromises = [];

    for (let i = 2; i <= firstResult.totalPages; i += 1) {
        queryPromises.push(query({ ...request, sort: [], page: i }));
    }

    const queryResults = await Promise.all(queryPromises);

    return [firstResult, ...queryResults];
}

export async function drainQueryCallback<TData, TFilter>(
    query: DataSourceQuery<TData, TFilter>,
    request: Pick<DataSourceRequest<TFilter>, 'filter' | 'pageLimit'>,
    callback: (value: DataSourceQueryResult<TData>) => void | PromiseLike<void>,
): Promise<void> {
    const startPage = 1;

    const firstResult = await query({ ...request, sort: [], page: startPage });
    await callback(firstResult);

    for (let i = 2; i <= firstResult.totalPages; i += 1) {
        query({ ...request, sort: [], page: i }).then(callback);
    }
}

/**
 * Used to query all results.
 *
 * @param query callback to query a specific page of results.
 * @param batchSize the number of allowable simultaneous queries (defaults to `1`).
 * @returns All results.
 */
export async function queryAll<TData>(
    query: (page: number) => Promise<PagedResult<TData>>,
    batchSize = 1,
): Promise<TData[]> {
    const firstResult = await query(1);

    if (firstResult.TotalPages === 1) {
        return firstResult.Result;
    }

    const restQueries = createRange(2, firstResult.TotalPages).map(
        (page) => async () => (await query(page)).Result,
    );
    const restResults = await promiseAllBatched<TData[]>(restQueries, batchSize);

    return [firstResult.Result, ...restResults].flatMap((result) => result);
}

/**
 * Splits a given search text string into tokens.
 * @param searchText The text to split into tokens.
 * @returns The tokens.
 */
export function splitSearchTextIntoTokens(searchText: string) {
    return unique(searchText.split(' '))
        .filter((token) => token !== '')
        .map((token) => token.toLowerCase());
}

/**
 * Constructs a filter expression that requires that every token in a search
 * string appear in at least one of the specified columns.
 * @param searchText The search text.
 * @param columnNames The columns to search.
 * @returns The search filter expression.
 */
export function constructSearchExpression(searchText: string, columnNames: string[]) {
    const tokens = splitSearchTextIntoTokens(searchText);

    return constructSearchExpressionFromTokens(tokens, columnNames);
}

type SearchMode = 'AllMatch' | 'AnyMatch';

function getFilterJoinerForSearch(mode: SearchMode) {
    switch (mode) {
        case 'AllMatch':
            return Filter.and;
        case 'AnyMatch':
            return Filter.or;
        default:
            throw new Error(`Unsupported search mode, '${mode}'`);
    }
}

export function constructSearchExpressionFromTokens(
    tokens: string[],
    columnNames: string[],
    mode: SearchMode = 'AllMatch',
) {
    return getFilterJoinerForSearch(mode)(
        ...tokens.map((token) =>
            Filter.or(
                ...columnNames.map((columnName) => Filter.contains(columnName, token)),
            ),
        ),
    );
}
